import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Select from 'components/Common/Select';
import ClockIcon from 'assets/icons/ClockIcon';
import Spinner from 'components/Common/Spinner';
import { zodResolver } from '@hookform/resolvers/zod';
import { llmSchema } from 'utils/schema/validator/AgencyConfigureValidation';
import { Box, Typography, Slider, Grid, Paper, Button, useTheme } from '@mui/material';
import { useGetAgencyConfiguration, useUpdateAgencyConfiguration } from 'apis/agency/configuration/hook';
import {
  useGetLlmServices,
  useGetServices,
  useGetSttServices,
  useGetTtsServices
} from 'apis/business/agent/services/hook';

export default function Llm() {
  const theme = useTheme();
  const [callMinutes, setCallMinutes] = useState(0);

  const { data: configuration, isLoading } = useGetAgencyConfiguration();
  const { mutateAsync: updateConfiguration } = useUpdateAgencyConfiguration();

  const { data: servicesData, isLoading: isLoadingServices } = useGetServices();
  const { data: sttServices, isLoading: isLoadingSttServices } = useGetSttServices();
  const { data: ttsServices, isLoading: isLoadingTtsServices } = useGetTtsServices();
  const { data: llmServices, isLoading: isLoadingLlmServices } = useGetLlmServices();

  const sttOptions = useMemo(() => {
    return (
      sttServices?.data?.map((item) => ({
        label: item,
        value: item
      })) ?? []
    );
  }, [sttServices?.data]);

  const llmOptions = useMemo(() => {
    return (
      llmServices?.data?.map((item) => ({
        label: item,
        value: item
      })) ?? []
    );
  }, [llmServices?.data]);

  const ttsOptions = useMemo(() => {
    return (
      ttsServices?.data?.map((item) => ({
        label: item,
        value: item
      })) ?? []
    );
  }, [ttsServices?.data]);

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: zodResolver(llmSchema),
    defaultValues: {
      configuration: {
        default_llm_service: '',
        default_tts_service: '',
        default_stt_service: ''
      }
    }
  });

  const formData = watch();
  const costPerMinute = Number((calculateCostPerMinute(servicesData?.data, formData?.configuration) / 100).toFixed(2));
  const totalCost = (callMinutes * costPerMinute).toFixed(2);

  const onSubmit = (data: any) => {
    updateConfiguration(data);
  };

  useEffect(() => {
    if (configuration) {
      reset({
        configuration: configuration
      });
    }
  }, [configuration, reset]);

  if (isLoading) return <Spinner />;

  return (
    <Box>
      <Typography variant='h6' fontWeight={700}>
        AI Services
      </Typography>
      <Typography color='text.secondary' mt={1}>
        Configure AI services including usage, Large Language Model, Text-to-Speech, and Speech-to-Text.
      </Typography>

      <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={3}>
        {/* Call Usage & Pricing */}
        <Paper variant='outlined' sx={{ p: 3, mb: 3 }}>
          <Box display='flex' alignItems='center' mb={2}>
            <Box mr={1} mt={1} color={theme.palette.secondary.light}>
              <ClockIcon />
            </Box>
            <Typography fontWeight={600} fontSize={20}>
              Call Usage & Pricing
            </Typography>
          </Box>

          <Typography gutterBottom>How many minutes of calls do you have per month?</Typography>
          <Slider
            valueLabelDisplay='auto'
            min={0}
            max={1000}
            step={10}
            onChange={(e: any) => {
              setCallMinutes(e?.target?.value);
            }}
            sx={{ color: theme.palette.primary.main }}
          />

          <Paper elevation={0} sx={{ mt: 2, mb: 3, backgroundColor: '#F9FAFB', p: 2, px: 4 }}>
            <Grid container justifyContent='space-between'>
              <Grid item>
                <Typography variant='body2' color='text.secondary'>
                  Cost Per Minute
                </Typography>
                {isLoadingServices ? 'loading...' : <Typography fontWeight={600}>${costPerMinute}</Typography>}
              </Grid>
              <Grid item>
                <Typography variant='body2' color='text.secondary'>
                  Total Estimated Cost
                </Typography>
                <Typography fontWeight={600}>${totalCost}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* AI Providers */}
          <Typography fontWeight={600} py={2} mt={3} borderTop={'1px solid #E5E7EB'}>
            AI Service Providers
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ServiceCard
                title='Large Language Model'
                description='Select your LLM provider'
                name='configuration.default_llm_service'
                control={control}
                options={llmOptions}
                loading={isLoadingLlmServices}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ServiceCard
                title='Text-to-Speech'
                description='Choose a TTS provider'
                name='configuration.default_tts_service'
                control={control}
                options={ttsOptions}
                loading={isLoadingTtsServices}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ServiceCard
                title='Speech-to-Text'
                description='Select your STT provider'
                name='configuration.default_stt_service'
                control={control}
                options={sttOptions}
                loading={isLoadingSttServices}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Actions */}
        <Box mt={4} display='flex' justifyContent='flex-end' gap={2}>
          <Button variant='contained' onClick={handleSubmit(onSubmit)}>
            Save Configuration
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

const cardStyle = {
  p: 2.5,
  borderRadius: 2,
  backgroundColor: '#F9FAFB',
  border: '1px solid #E5E7EB',
  minHeight: '160px'
};

type ServiceCardProps = {
  title: string;
  description: string;
  name: string;
  control: any;
  loading: boolean;
  options: { label: string; value: string }[];
};

const ServiceCard = ({ title, description, name, control, options, loading }: ServiceCardProps) => (
  <Paper elevation={0} sx={cardStyle}>
    <Typography fontWeight={600} fontSize={14} mb={0.5}>
      {title}
    </Typography>
    <Typography variant='body2' color='text.secondary' mb={1.5}>
      {description}
    </Typography>
    <Select name={name} control={control} label={''} sx={{ bgcolor: 'white' }} isLoading={loading} options={options} />
  </Paper>
);

const calculateCostPerMinute = (models: any, config: any): number => {
  const { default_llm_service, default_tts_service, default_stt_service } = config;
  const llmCost = models?.llm[default_llm_service]?.cost_per_minute_cents ?? 0;
  const ttsCost = models?.tts[default_tts_service]?.cost_per_minute_cents ?? 0;
  const sttCost = models?.stt[default_stt_service]?.cost_per_minute_cents ?? 0;
  const totalCost = llmCost + ttsCost + sttCost;
  return totalCost;
};
